import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Award, Star, Gift, MapPin, Utensils, Calendar, Crown, Zap } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function LoyaltyProgram() {
  const { user } = useContext(AuthContext);
  const [loyaltyData, setLoyaltyData] = useState(null);
  const [perks, setPerks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLoyaltyData();
    fetchPerks();
  }, []);

  const fetchLoyaltyData = async () => {
    try {
      const response = await axios.get(`${API}/loyalty/status`);
      setLoyaltyData(response.data);
    } catch (error) {
      console.error('Failed to fetch loyalty data:', error);
      toast.error('Failed to load loyalty program data');
    }
  };

  const fetchPerks = async () => {
    try {
      const response = await axios.get(`${API}/loyalty/perks`);
      setPerks(response.data);
    } catch (error) {
      console.error('Failed to fetch perks:', error);
    } finally {
      setLoading(false);
    }
  };

  const redeemPerk = async (perkId) => {
    try {
      const response = await axios.post(`${API}/loyalty/redeem/${perkId}`);
      toast.success(response.data.message);
      fetchLoyaltyData(); // Refresh loyalty data
      fetchPerks(); // Refresh available perks
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to redeem perk';
      toast.error(message);
    }
  };

  const getTierIcon = (tier) => {
    switch (tier) {
      case 'explorer': return <MapPin className="tier-icon" />;
      case 'adventurer': return <Zap className="tier-icon" />;
      case 'voyager': return <Star className="tier-icon" />;
      case 'elite_traveler': return <Crown className="tier-icon" />;
      default: return <Award className="tier-icon" />;
    }
  };

  const getTierColor = (tier) => {
    const colors = {
      explorer: '#8B5CF6',
      adventurer: '#06B6D4', 
      voyager: '#F59E0B',
      elite_traveler: '#10B981'
    };
    return colors[tier] || '#8B5CF6';
  };

  const getPerkIcon = (category) => {
    switch (category) {
      case 'dining': return <Utensils size={24} />;
      case 'entertainment': return <Star size={24} />;
      case 'attractions': return <MapPin size={24} />;
      case 'bonus_nights': return <Calendar size={24} />;
      default: return <Gift size={24} />;
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '40px 0', minHeight: 'calc(100vh - 70px)', background: '#f7fafc' }}>
        <div className="container">
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <div className="loading-spinner"></div>
            <p style={{ marginTop: '20px', color: '#718096' }}>Loading loyalty program...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!loyaltyData) {
    return (
      <div style={{ padding: '40px 0', minHeight: 'calc(100vh - 70px)', background: '#f7fafc' }}>
        <div className="container">
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <p style={{ color: '#718096' }}>Failed to load loyalty program data</p>
          </div>
        </div>
      </div>
    );
  }

  const progressPercentage = loyaltyData.points_to_next > 0 
    ? ((loyaltyData.current_points % 1000) / 1000) * 100 
    : 100;

  return (
    <div style={{ padding: '40px 0', minHeight: 'calc(100vh - 70px)', background: '#f7fafc' }}>
      <div className="container">
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <h1 style={{ 
            fontSize: '3rem', 
            fontWeight: '700', 
            color: '#2d3748',
            marginBottom: '16px'
          }}>
            🏆 ZypeeTravel Loyalty Program
          </h1>
          <p style={{ 
            fontSize: '1.25rem', 
            color: '#718096',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            Unlock exclusive perks and rewards as you travel more with ZypeeTravel!
          </p>
        </div>

        {/* Loyalty Status Card */}
        <div style={{ 
          background: 'white', 
          borderRadius: '20px', 
          padding: '40px',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
          marginBottom: '40px',
          border: `3px solid ${getTierColor(loyaltyData.current_tier)}`
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '32px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              <div style={{ 
                width: '80px', 
                height: '80px', 
                borderRadius: '50%', 
                background: `linear-gradient(135deg, ${getTierColor(loyaltyData.current_tier)}, ${getTierColor(loyaltyData.current_tier)}dd)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '2rem'
              }}>
                {getTierIcon(loyaltyData.current_tier)}
              </div>
              <div>
                <h2 style={{ 
                  fontSize: '2rem', 
                  fontWeight: '700', 
                  color: getTierColor(loyaltyData.current_tier),
                  marginBottom: '8px'
                }}>
                  {loyaltyData.tier_benefits.name}
                </h2>
                <p style={{ fontSize: '1.1rem', color: '#718096' }}>
                  {loyaltyData.current_points} loyalty points
                </p>
              </div>
            </div>
            
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: '600', color: '#2d3748', marginBottom: '4px' }}>
                {loyaltyData.total_bookings} trips
              </div>
              <div style={{ fontSize: '0.875rem', color: '#718096' }}>
                ${loyaltyData.total_spent?.toFixed(2)} total spent
              </div>
            </div>
          </div>

          {/* Progress to Next Tier */}
          {loyaltyData.next_tier && (
            <div style={{ marginBottom: '32px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '0.875rem', fontWeight: '500', color: '#4a5568' }}>
                  Progress to {loyaltyData.next_tier}
                </span>
                <span style={{ fontSize: '0.875rem', color: '#718096' }}>
                  {loyaltyData.points_to_next} points to go
                </span>
              </div>
              <div style={{ 
                width: '100%', 
                height: '8px', 
                background: '#e2e8f0', 
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <div style={{ 
                  height: '100%', 
                  background: `linear-gradient(90deg, ${getTierColor(loyaltyData.current_tier)}, ${getTierColor(loyaltyData.current_tier)}dd)`,
                  width: `${progressPercentage}%`,
                  transition: 'width 0.3s ease'
                }}></div>
              </div>
            </div>
          )}

          {/* Current Tier Benefits */}
          <div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '16px', color: '#2d3748' }}>
              Your Current Benefits
            </h3>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
              gap: '12px' 
            }}>
              {loyaltyData.tier_benefits.perks.map((perk, index) => (
                <div key={index} style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px',
                  padding: '12px',
                  background: '#f7fafc',
                  borderRadius: '8px'
                }}>
                  <div style={{ 
                    width: '8px', 
                    height: '8px', 
                    borderRadius: '50%', 
                    background: getTierColor(loyaltyData.current_tier)
                  }}></div>
                  <span style={{ color: '#4a5568', fontSize: '0.95rem' }}>{perk}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Available Perks */}
        <div style={{ 
          background: 'white', 
          borderRadius: '16px', 
          padding: '32px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
          marginBottom: '40px'
        }}>
          <h3 style={{ 
            fontSize: '1.75rem', 
            fontWeight: '600', 
            marginBottom: '24px',
            color: '#2d3748'
          }}>
            Available Perks
          </h3>
          
          {perks.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px', 
              color: '#718096'
            }}>
              <Gift size={48} style={{ margin: '0 auto 16px', opacity: 0.5 }} />
              <p>No perks available for your current tier</p>
            </div>
          ) : (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
              gap: '20px' 
            }}>
              {perks.map((perk) => (
                <div key={perk.id} style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '24px',
                  transition: 'all 0.3s ease'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                    <div style={{ 
                      padding: '8px', 
                      background: '#f7fafc', 
                      borderRadius: '8px',
                      color: '#10B981'
                    }}>
                      {getPerkIcon(perk.category)}
                    </div>
                    <h4 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#2d3748' }}>
                      {perk.title}
                    </h4>
                  </div>
                  
                  <p style={{ 
                    color: '#718096', 
                    fontSize: '0.95rem', 
                    lineHeight: '1.5',
                    marginBottom: '16px'
                  }}>
                    {perk.description}
                  </p>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      {perk.discount_percentage && (
                        <span style={{ 
                          color: '#10B981', 
                          fontWeight: '600',
                          fontSize: '0.875rem'
                        }}>
                          {perk.discount_percentage}% OFF
                        </span>
                      )}
                      {perk.points_cost && (
                        <span style={{ 
                          color: '#F59E0B', 
                          fontWeight: '600',
                          fontSize: '0.875rem'
                        }}>
                          {perk.points_cost} points
                        </span>
                      )}
                    </div>
                    
                    <button 
                      onClick={() => redeemPerk(perk.id)}
                      className="btn btn-primary"
                      style={{ padding: '8px 16px', fontSize: '0.875rem' }}
                      disabled={perk.points_cost && loyaltyData.current_points < perk.points_cost}
                    >
                      {perk.points_cost ? 'Redeem' : 'Active'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Transactions */}
        {loyaltyData.recent_transactions && loyaltyData.recent_transactions.length > 0 && (
          <div style={{ 
            background: 'white', 
            borderRadius: '16px', 
            padding: '32px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
          }}>
            <h3 style={{ 
              fontSize: '1.5rem', 
              fontWeight: '600', 
              marginBottom: '24px',
              color: '#2d3748'
            }}>
              Recent Activity
            </h3>
            
            <div style={{ display: 'grid', gap: '12px' }}>
              {loyaltyData.recent_transactions.slice(0, 5).map((transaction) => (
                <div key={transaction.id} style={{
                  padding: '16px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div>
                    <p style={{ fontWeight: '500', color: '#2d3748', marginBottom: '4px' }}>
                      {transaction.description}
                    </p>
                    <p style={{ fontSize: '0.875rem', color: '#718096' }}>
                      {new Date(transaction.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div style={{ 
                    color: transaction.points > 0 ? '#10B981' : '#EF4444',
                    fontWeight: '600'
                  }}>
                    {transaction.points > 0 ? '+' : ''}{transaction.points} pts
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default LoyaltyProgram;